/**
 * Mega menu tabs
 * ----------------------------------------------------------------------------
 * Drives the sidebar layout: hovering or focusing a tab on the left swaps which
 * panel of categories shows on the right.
 *
 * This file does one thing only: move a `data-active` attribute. It never reads
 * the viewport and never hides anything itself. CSS decides what `data-active`
 * means, and CSS is the only thing that knows the breakpoint.
 *
 * That split matters. An earlier version asked `matchMedia` whether it was on a
 * desktop before hiding panels, which put the breakpoint in two places and left
 * every panel stacked on top of each other anywhere the query answered
 * unexpectedly. Liquid marks the first tab and panel active, so the menu is
 * already correct before this file runs and stays correct if it never does.
 *
 * Two timing decisions:
 *
 * HOVER WAITS 120ms.
 *   The tabs sit on the path between the nav item and the grid, so the pointer
 *   crosses them on the way past. Swapping instantly makes the panel flicker
 *   through two or three states before the shopper has chosen anything. 120ms
 *   separates a pass from an intent without making a deliberate hover feel slow.
 *
 * FOCUS SWAPS IMMEDIATELY.
 *   A keyboard user has already committed by the time focus lands.
 *
 * The tabs stay plain anchors. Each one goes somewhere real, so clicking
 * navigates and hovering previews. An ARIA tablist would take arrow keys away
 * from their normal job and promise a widget this is not.
 */

(() => {
  const ACTIVATE_DELAY = 120;

  let pending;

  const activate = (tab) => {
    const root = tab.closest('.pebble-mega');
    if (!root) return;

    const id = tab.dataset.megaTab;

    root.querySelectorAll('[data-mega-tab]').forEach((other) => {
      other.toggleAttribute('data-active', other === tab);
    });

    root.querySelectorAll('[data-mega-panel]').forEach((panel) => {
      panel.toggleAttribute('data-active', panel.dataset.megaPanel === id);
    });
  };

  /**
   * Back to the first tab.
   *
   * Runs when a menu closes, so the next shopper to open it starts where the
   * last one started rather than wherever the previous pointer happened to
   * stop. A menu that reopens mid-thought is disorienting: the shopper expects
   * the thing they saw last time they opened it, not the thing they saw last
   * time they closed it.
   */
  const reset = (root) => {
    const first = root.querySelector('[data-mega-tab]');
    if (first) activate(first);
  };

  /*
   * Delegated, so panels re-rendered by the theme editor need no re-binding.
   * `pointerover` rather than `pointerenter` because only the former bubbles.
   */
  document.addEventListener('pointerover', (event) => {
    const tab = event.target.closest?.('[data-mega-tab]');
    if (!tab) return;

    clearTimeout(pending);
    pending = setTimeout(() => activate(tab), ACTIVATE_DELAY);
  });

  document.addEventListener('focusin', (event) => {
    const tab = event.target.closest?.('[data-mega-tab]');
    if (!tab) return;

    clearTimeout(pending);
    activate(tab);
  });

  /*
   * Horizon marks a closed submenu `inert`. Watching that attribute is how we
   * learn a menu has closed without knowing anything about Horizon's open and
   * close logic, which is free to change.
   */
  const watchForClose = () => {
    document.querySelectorAll('.menu-list__submenu').forEach((submenu) => {
      if (submenu.dataset.pebbleWatched) return;
      submenu.dataset.pebbleWatched = 'true';

      new MutationObserver(() => {
        if (!submenu.hasAttribute('inert')) return;

        const root = submenu.querySelector('.pebble-mega--sidebar');
        if (root) reset(root);
      }).observe(submenu, { attributeFilter: ['inert'] });
    });
  };

  document.addEventListener('shopify:section:load', watchForClose);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForClose, { once: true });
  } else {
    watchForClose();
  }
})();
